import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid } from 'semantic-ui-react'
import { useNavigate } from 'react-router-dom'

import { useReselector, useFetchResponse } from '@/utils/sharedHooks'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { selectAssetsOver2500ForYear } from '@/features/transactions/transactions.selectors'
import {
  PrefixedUserDocumentCategoryIdentifier,
  UserDocumentCategoryIdentifier,
} from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { getMatchingMerchants } from '../processing-fees-documents/documents-map'
import { Accordion, Text, GridRowColumn } from '@/componentsV1/BaseComponents'
import {
  DocumentValidationProvider,
  DocumentsValidationAlert,
  useShowDocumentErrors,
} from '../../components/document-validation'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import {
  IDocumentsStepContext,
  IFixedAssetDepreciationStepContext,
  OtherDocumentsStepContext,
  ContractorDocumentsStepContext,
  PayrollDocumentsStepContext,
  IProviders,
  EndOfYearAdminReviewStep,
} from '../types'
import {
  userUpdateBookkeepingStepContext,
  userGetBookkeepingSubmission,
  UserProcessingFee,
} from '../actions'
import { updateStepContext } from '../../services/documentUpload.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import type { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { migrateAssetStepContext } from '../admin/helpers'
import {
  useFilteredPayrollProviders,
  useDocumentEligibility,
  useStepContext,
} from '../../hooks'
import { PayrollDocument } from '../../components/document-types/payroll-document'
import { ContractorDocument } from '../../components/document-types/contractor-document'
import { ProcessingFeesDocument } from '../../components/document-types/processing-fees-document'
import { AssetDepreciationDocument } from '../../components/document-types/asset-depreciation-document'
import { TaxReturnDocument } from '../../components/document-types/tax-return-document'
import { OtherDocuments } from '../../components/document-types/other-documents'
import './styles.scss'

export interface IReviewDocumentsProps {
  providers: IProviders
  taxYear: string
  processingFees?: UserProcessingFee[]
  isMultiYearSCorp: boolean
  documents: UserDocument[]
  setIsStepComplete: (value: boolean) => void
  isFollowUp?: boolean
  form1120sMissing?: boolean
  isTaxCategoryDocs?: boolean
}

export const ReviewDocuments = ({
  documents,
  setIsStepComplete,
  taxYear,
  processingFees = [],
  isMultiYearSCorp,
  isFollowUp = false,
  form1120sMissing = false,
  isTaxCategoryDocs = true,
}: IReviewDocumentsProps) => {
  const navigate = useNavigate()
  const showValidationError = useShowDocumentErrors()
  const dispatch = useAppDispatch()
  const year = useReselector(selectCurrentAnnualTaxYear)

  // State for contractor "Other documents" upload
  const [contractorOtherDocsModalOpen, setContractorOtherDocsModalOpen] =
    useState(false)

  // Select assets over $2,500 for the tax year
  const assetsOver2500 = useReselector(selectAssetsOver2500ForYear, taxYear)

  // Fetch submission to get step statuses
  const submission = useFetchResponse(userGetBookkeepingSubmission)

  // Extract step statuses for all document types
  const payrollStepStatus = useMemo(() => {
    if (!submission?.steps) return undefined
    const step = submission.steps.find(
      (s) => s.step === EndOfYearAdminReviewStep.docsPayroll
    )
    return step?.status
  }, [submission])

  const contractorStepStatus = useMemo(() => {
    if (!submission?.steps) return undefined
    const step = submission.steps.find(
      (s) => s.step === EndOfYearAdminReviewStep.docsContractorPayments
    )
    return step?.status
  }, [submission])

  const processingFeesStepStatus = useMemo(() => {
    if (!submission?.steps) return undefined
    const step = submission.steps.find(
      (s) => s.step === EndOfYearAdminReviewStep.docsProcessingFees
    )
    return step?.status
  }, [submission])

  const assetStepStatus = useMemo(() => {
    if (!submission?.steps) return undefined
    const step = submission.steps.find(
      (s) => s.step === EndOfYearAdminReviewStep.docsAssetsOver2500
    )
    return step?.status
  }, [submission])

  const taxStepStatus = useMemo(() => {
    if (!submission?.steps) return undefined
    const step = submission.steps.find(
      (s) => s.step === EndOfYearAdminReviewStep.docsPreviousTaxReturn
    )
    return step?.status
  }, [submission])

  // Fetch all step contexts from Redux
  const { data: payrollStepContextRaw } = useStepContext(
    EndOfYearAdminReviewStep.docsPayroll
  )
  const { data: contractorStepContextRaw } = useStepContext(
    EndOfYearAdminReviewStep.docsContractorPayments
  )
  const { data: feesStepContextRaw } = useStepContext(
    EndOfYearAdminReviewStep.docsProcessingFees
  )
  const { data: rawAssetStepContextRaw } = useStepContext(
    EndOfYearAdminReviewStep.docsAssetsOver2500
  )
  const { data: taxStepContextRaw } = useStepContext(
    EndOfYearAdminReviewStep.docsPreviousTaxReturn
  )
  const { data: otherDocsContextRaw } = useStepContext(
    EndOfYearAdminReviewStep.docsOther
  )

  // Convert null to undefined for component compatibility
  const payrollStepContext = (payrollStepContextRaw || undefined) as
    | PayrollDocumentsStepContext
    | undefined
  const contractorStepContext = (contractorStepContextRaw || undefined) as
    | ContractorDocumentsStepContext
    | undefined
  const feesStepContext = (feesStepContextRaw || undefined) as
    | IDocumentsStepContext
    | undefined
  const rawAssetStepContext = (rawAssetStepContextRaw || undefined) as
    | IFixedAssetDepreciationStepContext
    | undefined
  const taxStepContext = (taxStepContextRaw || undefined) as
    | IDocumentsStepContext
    | undefined
  const otherDocsContext = (otherDocsContextRaw || undefined) as
    | OtherDocumentsStepContext
    | undefined

  // Migrate legacy asset step context format
  const assetStepContext = useMemo(
    () => migrateAssetStepContext(rawAssetStepContext),
    [rawAssetStepContext]
  )

  // Extract provider arrays from step contexts
  const payrollProviders = useMemo(
    () =>
      Array.isArray(payrollStepContext?.payrollProviders)
        ? payrollStepContext.payrollProviders
        : [],
    [payrollStepContext]
  )

  // Backward compatibility: read from contractorProviders, fall back to payrollProviders (old field name)
  const contractorProviders = useMemo(
    () =>
      Array.isArray(contractorStepContext?.contractorProviders)
        ? contractorStepContext.contractorProviders
        : Array.isArray(contractorStepContext?.payrollProviders)
          ? contractorStepContext.payrollProviders
          : [],
    [contractorStepContext]
  )

  // Merchant and transaction processing
  const uniqueDocumentMerchants = useMemo(
    () => getMatchingMerchants(taxYear, processingFees),
    [taxYear, processingFees]
  )

  const transactionsWithoutReceipts = useMemo(() => {
    return assetsOver2500
      .filter(
        (transaction) =>
          (transaction?.receipts && transaction?.receipts.length === 0) ||
          !transaction?.receipts
      )
      .map((transaction) => ({
        ...transaction,
        receipts: transaction?.receipts?.map((doc) => ({ documentId: doc.id })),
      }))
  }, [assetsOver2500])

  const receipts = useMemo(() => {
    if (!assetStepContext) return []
    const uploadedReceipts = assetStepContext?.receipts ?? []
    return documents.filter((doc) =>
      uploadedReceipts.some((r) => r.documentId === doc.id)
    )
  }, [assetStepContext, documents])

  // Category identifiers
  const categoryIdentifiers = useMemo(
    () => ({
      formW3: UserDocumentCategoryIdentifier.formW3,
      payrollJournal:
        PrefixedUserDocumentCategoryIdentifier.payrollJournal(year),
      contractorPaymentsReport:
        PrefixedUserDocumentCategoryIdentifier.contractorPaymentsReport(year),
      form1099nec: UserDocumentCategoryIdentifier.form1099nec,
    }),
    [year]
  )

  // Determine the visibility of document categories based on context and follow-up state
  const filteredPayrollProviders = useFilteredPayrollProviders(
    payrollStepContext,
    isFollowUp
  )
  const filteredContractorProviders = useFilteredPayrollProviders(
    contractorStepContext,
    isFollowUp
  )

  const shouldShowFees = uniqueDocumentMerchants.length > 0
  const shouldShowAssets =
    (transactionsWithoutReceipts.length > 0 && !isFollowUp) ||
    (isFollowUp &&
      (receipts.length > 0 ||
        (assetStepContext?.receipts && assetStepContext.receipts.length > 0)))

  const shouldShowTax = useMemo(() => {
    // Only show for multi-year S-Corps
    if (!isMultiYearSCorp) return false

    // In normal mode, show if tax docs are needed
    if (!isFollowUp) {
      return form1120sMissing || isTaxCategoryDocs
    }

    // In follow-up mode, only show if there's a document that needs re-upload or has been updated
    const taxDocument = taxStepContext?.documents?.find(
      (doc) =>
        doc.categoryCode === UserDocumentCategoryIdentifier.finalReturnForm1120s
    )

    // Show if document needs re-upload or has been updated by user
    return (taxDocument?.requestReUpload || taxDocument?.updatedByUser) ?? false
  }, [
    isFollowUp,
    isMultiYearSCorp,
    form1120sMissing,
    isTaxCategoryDocs,
    taxStepContext?.documents,
  ])

  const shouldShowOther = !isFollowUp

  // Collect "Other Documents" from all document step contexts for display in summary
  const additionalOtherDocumentIds = useMemo(
    () => [
      ...(payrollStepContext?.otherDocumentIds ?? []),
      ...(contractorStepContext?.otherDocumentIds ?? []),
      ...(assetStepContext?.otherDocumentIds ?? []),
    ],
    [payrollStepContext, contractorStepContext, assetStepContext]
  )

  // Calculate document eligibility to determine if user can proceed
  const { canContinue, unresolvedDocuments } = useDocumentEligibility({
    stepContexts: {
      payroll: payrollStepContext,
      contractor: contractorStepContext,
      fees: feesStepContext,
      assets: assetStepContext,
      tax: taxStepContext,
      other: otherDocsContext,
    },
    payrollProviders,
    contractorProviders,
    uniqueDocumentMerchants,
    transactionsWithoutReceipts,
    documents,
    isMultiYearSCorp,
    isFollowUp,
    categoryIdentifiers,
    shouldShowTax,
  })

  useEffect(() => {
    setIsStepComplete(canContinue)
  }, [canContinue, setIsStepComplete])

  // Navigate away if no documents needed
  useEffect(() => {
    if (
      payrollProviders?.length === 0 &&
      contractorProviders?.length === 0 &&
      uniqueDocumentMerchants.length === 0 &&
      receipts?.length === 0 &&
      !isTaxCategoryDocs &&
      !form1120sMissing
    ) {
      navigate(
        `/taxes/annual/complete-year-end-bookkeeping/${year}/first-submission`
      )
    }
  }, [
    payrollProviders,
    contractorProviders,
    uniqueDocumentMerchants,
    receipts,
    form1120sMissing,
    isTaxCategoryDocs,
    navigate,
    year,
  ])

  // Callback handlers for step context updates
  // Updates Redux state immediately, then persists to API
  const handleSetPayrollContext = useCallback(
    async (context: PayrollDocumentsStepContext) => {
      dispatch(updateStepContext({ stepId: 'docs-payroll', context }))
      await userUpdateBookkeepingStepContext(
        'docs-payroll',
        'docs-payroll',
        context
      )(dispatch)
    },
    [dispatch]
  )

  const handleSetContractorContext = useCallback(
    async (context: ContractorDocumentsStepContext) => {
      dispatch(
        updateStepContext({ stepId: 'docs-contractor-payments', context })
      )
      await userUpdateBookkeepingStepContext(
        'docs-contractor-payments',
        'docs-contractor-payments',
        context
      )(dispatch)
    },
    [dispatch]
  )

  const handleContractorOtherDocsUploaded = useCallback(
    (uploadedFile: UploadedFile) => {
      const otherDocumentIds = [
        ...(contractorStepContext?.otherDocumentIds ?? []),
        uploadedFile.id,
      ]
      handleSetContractorContext({
        ...(contractorStepContext ?? {}),
        otherDocumentIds,
      })
      setContractorOtherDocsModalOpen(false)
    },
    [contractorStepContext, handleSetContractorContext]
  )

  const handleSetFeesContext = useCallback(
    async (context: IDocumentsStepContext) => {
      dispatch(updateStepContext({ stepId: 'docs-processing-fees', context }))
      await userUpdateBookkeepingStepContext(
        'docs-processing-fees',
        'docs-processing-fees',
        context
      )(dispatch)
    },
    [dispatch]
  )

  const handleSetAssetContext = useCallback(
    async (context: IFixedAssetDepreciationStepContext) => {
      dispatch(
        updateStepContext({
          stepId: 'docs-assets-over-2500',
          context,
        })
      )
      await userUpdateBookkeepingStepContext(
        'docs-assets-over-2500',
        'docs-assets-over-2500',
        context
      )(dispatch)
    },
    [dispatch]
  )

  const handleSetTaxContext = useCallback(
    async (context: IDocumentsStepContext) => {
      dispatch(
        updateStepContext({ stepId: 'docs-previous-tax-return', context })
      )
      await userUpdateBookkeepingStepContext(
        'docs-previous-tax-return',
        'docs-previous-tax-return',
        context
      )(dispatch)
    },
    [dispatch]
  )

  const handleSetOtherContext = useCallback(
    async (context: OtherDocumentsStepContext) => {
      dispatch(
        updateStepContext({
          stepId: 'docs-other',
          context,
        })
      )
      await userUpdateBookkeepingStepContext(
        'docs-other',
        'docs-other',
        context
      )(dispatch)
    },
    [dispatch]
  )

  const taxYearInt = parseInt(taxYear)

  return (
    <div className="eoybk__payroll__container">
      <DocumentsValidationAlert unresolvedDocuments={unresolvedDocuments} />
      <StepHeaders
        customColor="yellow"
        description="Based on your answers, we'll need the following documents to complete your bookkeeping for the year."
        imageSrc="https://heard-images.s3.amazonaws.com/assets/upload-document.svg"
        imageAlt="stacked checkbooks"
        imageWidth={64}
        imageHeight={61}
        title="Upload documents"
        isAbstractHeader
      >
        <Grid>
          <GridRowColumn className="align-left">
            <Text>
              Your progress will be saved, so feel free to take your time and
              come back later if needed.
            </Text>
          </GridRowColumn>
          <GridRowColumn className="align-left">
            <Accordion
              title="What if I'm having trouble finding documents? "
              content={
                <Text>
                  We ask that you take your time and make your best effort, as
                  these documents are needed to accurately complete your books.
                  <b>
                    This
                    <span> directly impacts your annual tax filing </span>
                  </b>{' '}
                  and can help avoid underreporting penalties.
                </Text>
              }
              variant="text"
            />
          </GridRowColumn>
        </Grid>
      </StepHeaders>

      {/* Payroll Documents - render one section per selected provider */}
      <DocumentValidationProvider
        showErrors={
          showValidationError &&
          unresolvedDocuments.some((doc) => doc.section === 'payroll')
        }
      >
        {filteredPayrollProviders.map((provider) => (
          <PayrollDocument
            key={provider}
            stepContext={payrollStepContext}
            setStepContext={handleSetPayrollContext}
            provider={provider}
            userDocuments={documents}
            isFollowUp={isFollowUp}
            taxYear={parseInt(taxYear)}
            stepStatus={payrollStepStatus}
          />
        ))}
      </DocumentValidationProvider>

      {/* Contractor Documents - unified section with all providers */}
      {Boolean(filteredContractorProviders.length) &&
        (!isFollowUp || contractorStepStatus === 'user_action_required') && (
          <div className="contractor-payments-section">
            <h2>Contractor Payments</h2>
            <div className="contractor-payments-section__cards">
              {filteredContractorProviders.map((provider) => (
                <ContractorDocument
                  key={provider}
                  stepContext={contractorStepContext}
                  setStepContext={handleSetContractorContext}
                  provider={provider}
                  userDocuments={documents}
                  isFollowUp={isFollowUp}
                  taxYear={taxYearInt}
                  showHeader={false}
                  stepStatus={contractorStepStatus}
                />
              ))}
              {/* Other documents for contractor payments */}
              <DocumentUploadCard
                name="Other documents"
                description="Upload any other documents you'd like to share with the bookkeeping team here."
                status="disabled"
                documents={documents.filter((doc) =>
                  (contractorStepContext?.otherDocumentIds ?? []).includes(
                    doc.id
                  )
                )}
                handleUploadBtn={() => setContractorOtherDocsModalOpen(true)}
              />
            </div>
            <FileUploadModal
              open={contractorOtherDocsModalOpen}
              close={() => setContractorOtherDocsModalOpen(false)}
              documentType={UploadDocumentType.OTHER}
              userFacing
              year={taxYear}
              setUploadedFile={handleContractorOtherDocsUploaded}
              isUploadOnly
            />
          </div>
        )}

      {/* Processing Fees */}
      {shouldShowFees && (
        <ProcessingFeesDocument
          stepContext={feesStepContext}
          setStepContext={handleSetFeesContext}
          userDocuments={documents}
          isFollowUp={isFollowUp}
          taxYear={taxYearInt}
          processingFees={processingFees}
          stepStatus={processingFeesStepStatus}
        />
      )}

      {/* Asset Depreciation */}
      {shouldShowAssets && (
        <AssetDepreciationDocument
          stepContext={assetStepContext}
          setStepContext={handleSetAssetContext}
          userDocuments={documents}
          isFollowUp={isFollowUp}
          taxYear={taxYearInt}
          stepStatus={assetStepStatus}
        />
      )}

      {/* Tax Return */}
      {shouldShowTax && (
        <TaxReturnDocument
          stepContext={taxStepContext}
          setStepContext={handleSetTaxContext}
          userDocuments={documents}
          isFollowUp={isFollowUp}
          taxYear={taxYearInt}
          stepStatus={taxStepStatus}
        />
      )}

      {/* Other Documents */}
      {shouldShowOther && (
        <OtherDocuments
          stepContext={otherDocsContext}
          setStepContext={handleSetOtherContext}
          userDocuments={documents}
          isFollowUp={isFollowUp}
          taxYear={taxYearInt}
          additionalOtherDocumentIds={additionalOtherDocumentIds}
        />
      )}
    </div>
  )
}
